"use client";

import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useState } from "react";

import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "./tooltip";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

function shouldShowRelativeTime(
	now: dayjs.Dayjs,
	parsedDate: dayjs.Dayjs,
	showRelativeTime: boolean,
): boolean {
	const diffInDays = now.diff(parsedDate, "day");
	return diffInDays < 1 && showRelativeTime;
}

interface FormattedTimeProps {
	date: string;
	showRelativeTime?: boolean;
}

export function FormattedTime({
	date,
	showRelativeTime = false,
}: FormattedTimeProps) {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const parsedDate = useMemo(() => {
		return dayjs.utc(date);
	}, [date]);

	const formattedDateTime = useMemo(() => {
		const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const localDate = parsedDate.tz(userTimezone);
		return localDate.format("MMM D, YYYY h:mm:ss A");
	}, [parsedDate]);

	const [now, setNow] = useState(() => {
		return dayjs.utc();
	});

	const isShowingRelativeTime = shouldShowRelativeTime(
		now,
		parsedDate,
		showRelativeTime,
	);

	useEffect(() => {
		if (!isShowingRelativeTime) {
			return;
		}

		const intervalId = setInterval(() => {
			setNow(dayjs.utc());
		}, 1000);

		return () => {
			clearInterval(intervalId);
		};
	}, [isShowingRelativeTime]);

	if (!mounted) {
		// Show a skeleton while mounting to avoid flashing time with incorrect timezone.
		// Use UTC date as invisible content to provide natural width and height.
		return (
			<span className="relative inline-block">
				<span className="sr-only">Loading timestamp...</span>
				<span aria-hidden="true" className="opacity-0">
					{formattedDateTime}
				</span>
				<span className="bg-brand-grey/25 absolute inset-0 animate-pulse rounded" />
			</span>
		);
	}

	if (!isShowingRelativeTime) {
		return <span>{formattedDateTime}</span>;
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild={true}>
				{/* Derived, not stored: a changed `date` has to re-read on the same
				    render, or the previous date's wording stays on screen. */}
				<span className="relative cursor-default">{parsedDate.from(now)}</span>
			</TooltipTrigger>
			<TooltipContent>
				<p>{formattedDateTime}</p>
			</TooltipContent>
		</Tooltip>
	);
}
